import React from 'react';
import type { CFSummary, CFTopEntry, CFMonthlyData, CFQuery } from '../../types/campaignFinance';
import { fmtMoney } from '../../utils/campaignFinanceUtils';
import CFTrendChart from './CFTrendChart';
import CFExplorerPanel from './CFExplorerPanel';
import StatCard, { StatCardRow } from '../StatCard';
import CommitteeLink from '../committee/CommitteeLink';

interface CFContributorDashboardProps {
  contributorName: string;
  summary: CFSummary | null;
  topRecipients: CFTopEntry[];
  monthlyTrend: CFMonthlyData[];
  /** The searched query — scopes the contributions explorer below. */
  query: CFQuery;
  cycleLabel?: string;
  /** Forwarded to the explorer: Source / Counterparty cells launch a new search. */
  onEntitySearch?: (kind: 'contributor' | 'committee', name: string) => void;
}

const eyebrow = 'text-[10.5px] font-bold uppercase tracking-[0.12em] text-accent-500 dark:text-text-dark-tertiary';

/**
 * Contributor (donor) result — the outbound sibling of the committee dashboard.
 * A donor only sends money out, so there is no raised/spent/cash/burn or waterfall;
 * the metrics are giving stats and the flow is one-directional (contributor → recipients).
 */
const CFContributorDashboard: React.FC<CFContributorDashboardProps> = ({
  contributorName, summary, topRecipients, monthlyTrend, query, cycleLabel, onEntitySearch,
}) => {
  const recipients = topRecipients.slice(0, 6);
  const maxRecipient = Math.max(1, ...recipients.map(r => Math.abs(r.total)));
  const grand = summary?.total || 0;

  return (
    <div className="space-y-[18px]">
      {/* Header */}
      <div className="card !p-0 overflow-hidden">
        <div className="p-5 px-6">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="font-semibold text-[23px] tracking-[-0.02em] text-primary-800 dark:text-text-dark-primary">
              {contributorName}
            </span>
            <span className="rounded-full bg-primary-100 dark:bg-primary-dark-100/40 text-primary-700 dark:text-primary-dark-400 px-2.5 py-[3px] text-[11px] font-semibold">
              Contributor
            </span>
          </div>
          <div className="text-[12.5px] text-accent-500 dark:text-text-dark-tertiary mt-2.5">
            Outbound giving{cycleLabel ? ` · ${cycleLabel} cycle` : ''}
          </div>
        </div>
      </div>

      {/* KPI strip — donor metrics */}
      {summary && (
        <StatCardRow minTileWidth="11rem">
          <StatCard label="Total Given" value={fmtMoney(summary.total, { compact: true })} variant="success" />
          <StatCard label="Recipients" value={summary.recipients.toLocaleString()} />
          <StatCard label="Contributions" value={summary.count.toLocaleString()} />
          <StatCard label="Largest Gift" value={fmtMoney(summary.largest, { compact: true })} variant="info" />
          <StatCard label="Average Gift" value={fmtMoney(summary.avg, { compact: true })} variant="info" />
          <StatCard label="Median Gift" value={fmtMoney(summary.median, { compact: true })} variant="info" />
        </StatCardRow>
      )}

      {/* Follow the money — outbound */}
      {summary && (
        <div className="card">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <span className={eyebrow}>Follow the money — outbound</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-[18px] items-start">
            {/* Contributor node */}
            <div className="flex flex-col items-center gap-2">
              <div className="rounded-[14px] px-3 py-3.5 text-center w-full text-white" style={{ background: 'var(--cf-out-deep)', boxShadow: '0 6px 20px rgba(62,39,80,.3)' }}>
                <div className="text-[11px] font-semibold tracking-[0.04em] text-cf-out-pale">CONTRIBUTOR</div>
                <div className="font-semibold text-[13px] leading-tight mt-[3px] truncate" title={contributorName}>{contributorName}</div>
                <div className="mt-2.5">
                  <div className="text-[9px] font-semibold text-cf-out-muted">GIVEN</div>
                  <div className="font-bold text-sm text-cf-in-bright tabular-nums">{fmtMoney(summary.total, { compact: true })}</div>
                </div>
              </div>
              <svg width="100%" height="22" viewBox="0 0 200 22" preserveAspectRatio="none" className="hidden lg:block">
                <line x1="2" y1="11" x2="186" y2="11" stroke="var(--cf-in)" strokeWidth="2" />
                <path d="M180 5 L192 11 L180 17" fill="none" stroke="var(--cf-in)" strokeWidth="2" />
              </svg>
            </div>
            {/* Top recipients */}
            <div>
              <div className="flex items-center gap-[7px] mb-3">
                <span className="w-2 h-2 rounded-full bg-cf-in" />
                <span className="text-xs font-semibold text-cf-in-text">Top recipients</span>
              </div>
              <div className="flex flex-col gap-2">
                {recipients.length === 0 && <div className="text-[11.5px] text-accent-400">No recipients in range.</div>}
                {recipients.map(r => {
                  const share = grand > 0 ? (Math.abs(r.total) / grand) * 100 : 0;
                  return (
                    <div key={r.name}>
                      <div className="flex justify-between items-baseline mb-[3px] gap-2">
                        {/* Recipient → the committee card, whose "Open full dashboard" is the
                            return hop to the committee view (PRD §5.12, O3). */}
                        <CommitteeLink
                          coId={r.co_id}
                          name={r.name}
                          className="text-[12.5px] text-text-primary dark:text-text-dark-primary truncate min-w-0 flex-1 hover:text-primary-700 dark:hover:text-primary-dark-400"
                        />
                        <span className="tabular-nums text-xs font-semibold flex-none text-cf-in-text">
                          {share >= 0.1 ? `${share.toFixed(1)}%` : ''} {fmtMoney(r.total, { compact: true })}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden bg-cf-in-soft">
                        <div className="h-full rounded-full" style={{ width: `${Math.max(2, (Math.abs(r.total) / maxRecipient) * 100)}%`, background: 'var(--cf-in)' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Monthly giving trend */}
      {monthlyTrend.length > 0 && (
        <CFTrendChart data={monthlyTrend} title="Monthly giving" />
      )}

      {/* Contributions ledger (new explorer table, contributions only for a donor) */}
      <CFExplorerPanel query={query} cycleLabel={cycleLabel} onEntitySearch={onEntitySearch} />
    </div>
  );
};

export default CFContributorDashboard;
